// Maps the raw analytics wire payload (RawAnalyticsViewDto) to the AnalyticsViewDto view
// model the tiles consume (ORB-373). The API nests the M1 visitor section under `Visitors`
// as bare numbers and exposes attribution as flat arrays; the tiles expect flat
// AnalyticsMetricDto fields, an AttributionItemDto[] for lists, and a keyed ChannelBreakdownDto.
//
// Because the API provides only a current count for visitor metrics (no PreviousWindow/Delta),
// each is wrapped as `{ Current }` — leaving Delta undefined, so the stat tiles render without
// a growth pill (handled by AnalyticsStatTile).

import type {
  AnalyticsMetricDto, AnalyticsViewDto, AttributionItemDto, AttributionView,
  ChannelBreakdownDto, AnalyticsChannel, RawAnalyticsViewDto, RawAttributionRowDto,
  EngagementMetricsDto, RawEngagementMetricsDto, ScrollDepthBucketDto,
} from '@/types/analytics'

/** Wrap a bare wire count as a delta-less metric. */
const metric = (n: number | undefined): AnalyticsMetricDto => ({ Current: n ?? 0 })

/** Wrap a [0,1] wire fraction as a percentage metric — the view model carries 0–100. */
const percentMetric = (frac: number | undefined): AnalyticsMetricDto => ({ Current: (frac ?? 0) * 100 })

/** Pick the first non-empty source of breakdown rows (top-level array vs Attribution.By*). */
function firstNonEmpty(...candidates: (RawAttributionRowDto[] | undefined)[]): RawAttributionRowDto[] {
  for (const rows of candidates) {
    if (rows && rows.length) return rows
  }
  return []
}

/** Map raw breakdown rows ({ Key, Count }) to the AttributionItemDto[] the list tiles consume. */
function toAttributionItems(rows: RawAttributionRowDto[]): AttributionItemDto[] {
  return rows.map(r => ({
    Label: r.Key ?? '',
    Sessions: metric(r.Count),
  }))
}

/** Fold the channel breakdown rows into the keyed ChannelBreakdownDto object the tile expects. */
function toChannelBreakdown(rows: RawAttributionRowDto[]): ChannelBreakdownDto | undefined {
  if (!rows.length) return undefined
  // Channel keys arrive lowercase (e.g. "email"); match case-insensitively to the typed keys.
  const countByChannel = new Map<string, number>()
  for (const r of rows) {
    if (r.Key) countByChannel.set(r.Key.toLowerCase(), r.Count ?? 0)
  }
  const pick = (c: AnalyticsChannel) => metric(countByChannel.get(c.toLowerCase()))
  return { Email: pick('Email'), Paid: pick('Paid'), Social: pick('Social'), Organic: pick('Organic'), Direct: pick('Direct') }
}

/**
 * Map the raw engagement section to the view model (ORB-374). `AvgTimeOnPageSeconds` and
 * `AvgMaxScrollPct` arrive ready-to-render (seconds / 0–100) so they pass through `metric()`
 * unscaled — unlike the M1 conversion rates, which are fractions needing `percentMetric`. The
 * scroll funnel rows ({ Key: "50", Count }) become typed buckets ordered by threshold ascending.
 */
function toEngagementMetrics(raw: RawEngagementMetricsDto | undefined): EngagementMetricsDto | undefined {
  if (!raw) return undefined
  const funnel: ScrollDepthBucketDto[] = (raw.ScrollDepthFunnel ?? [])
    .map(r => ({ ThresholdPct: Number(r.Key ?? 0), Events: metric(r.Count) }))
    .sort((a, b) => a.ThresholdPct - b.ThresholdPct)
  return {
    AvgTimeOnPageSeconds: metric(raw.AvgTimeOnPageSeconds),
    AvgMaxScrollPct: metric(raw.AvgMaxScrollPct),
    // ORB-534: left undefined on an older API rather than defaulted to 0 — the caller renders the
    // denominator only when it is real, and "over 0 sessions" would be a worse caption than none.
    ScrolledSessions: raw.ScrolledSessions,
    ScrollDepthFunnel: funnel,
    CtaClicks: metric(raw.CtaClicks),
    DownloadClicks: metric(raw.DownloadClicks),
    VideoPlays: metric(raw.VideoPlays),
    PageEngagementEvents: raw.PageEngagementEvents ?? 0,
  }
}

/** Numeric wire enum → AttributionView string. Order matches ATTRIBUTION_VIEWS (0 = ConversionSession). */
function toAttributionView(view: number | undefined): AttributionView | undefined {
  if (view === 0) return 'ConversionSession'
  if (view === 1) return 'FirstSeen'
  return undefined
}

/** Translate the raw analytics payload into the tile-facing view model. */
export function normalizeAnalyticsView(raw: RawAnalyticsViewDto): AnalyticsViewDto {
  const visitors = raw.Visitors
  // Prefer the top-level breakdown arrays, falling back to Attribution.By* (one is canonical;
  // both appear on the wire). firstNonEmpty makes this correct whichever the API populates.
  const channelRows = firstNonEmpty(raw.Channels, raw.Attribution?.ByChannel)
  const referrerRows = firstNonEmpty(raw.TopReferrers, raw.Attribution?.BySource, raw.SessionsBySource)
  const campaignRows = firstNonEmpty(raw.TopUtmCampaigns, raw.Attribution?.ByCampaign, raw.SessionsByCampaign)

  return {
    // P2 funnel — already in view-model shape. UniqueLandingPageViews (ORB-534) passes through
    // undefined on an API that predates it, which the funnel treats as "fall back to Thank-You".
    LandingPageViews: raw.LandingPageViews,
    UniqueLandingPageViews: raw.UniqueLandingPageViews,
    ThankYouPageViews: raw.ThankYouPageViews,
    Conversions: raw.Conversions,

    // M1 visitor metrics — bare numbers wrapped as delta-less metrics.
    UniqueVisitors: visitors ? metric(visitors.UniqueVisitors) : undefined,
    UniqueKnownVisitors: visitors ? metric(visitors.KnownVisitors) : undefined,
    UniqueUnknownVisitors: visitors ? metric(visitors.UnknownVisitors) : undefined,
    ReturningVisitors: visitors ? metric(visitors.ReturningVisitors) : undefined,
    Sessions: visitors ? metric(visitors.Sessions) : undefined,
    ConversionRate: visitors ? percentMetric(visitors.ConversionRate) : undefined,
    AvgSessionDurationSeconds: visitors ? metric(visitors.AvgSessionDurationSeconds) : undefined,

    // Attribution breakdowns.
    TopReferrers: toAttributionItems(referrerRows),
    TopUtmCampaigns: toAttributionItems(campaignRows),
    Channels: toChannelBreakdown(channelRows),

    // Aggregate unknown reporting (renamed wire fields).
    Unknown: raw.Unknown
      ? {
          UniqueVisitors: metric(raw.Unknown.UnknownVisitors),
          Sessions: metric(raw.Unknown.UnknownSessions),
          ConversionRate: percentMetric(raw.Unknown.UnknownToKnownConversionRate),
        }
      : undefined,

    // Engagement metrics (M2).
    Engagement: toEngagementMetrics(raw.Engagement),

    AttributionView: toAttributionView(raw.Attribution?.View),
  }
}
